import {
	TPgClientAccessArea,
	TPgClientAccessSettings,
	TPgClientAccessSettingsRequest,
	TPgClientNotificationSetting,
} from 'services/api/paymentGateway/types';

/** Limit of a "blocked" message, checked again by the gateway. */
export const ACCESS_MESSAGE_MAX_LENGTH = 1000;

export type TAccessAreaKey = 'payLink' | 'paymentMethods' | 'payments' | 'euDdp';

export const ACCESS_AREA_KEYS: TAccessAreaKey[] = [
	'payLink',
	'paymentMethods',
	'payments',
	'euDdp',
];

export const ACCESS_AREAS: Record<TAccessAreaKey, { title: string; description: string }> = {
	payLink: {
		title: 'Payment link (pay page)',
		description:
			'Customers can pay invoices via the public payment link. When blocked, the pay page shows the message instead of the payment form.',
	},
	paymentMethods: {
		title: 'Payment methods (merchant dashboard)',
		description:
			'Customers can add, delete and change payment methods in the merchant dashboard: cards, wallets, Direct Debit mandates, the default method and the services of a method. When blocked, these screens are read-only and show the message.',
	},
	payments: {
		title: 'Payments (merchant dashboard)',
		description:
			'Customers can make payments themselves in the merchant dashboard (Make payment), and the gateway charges open invoices right after a new payment method is added. When blocked, the dashboard shows the message.',
	},
	euDdp: {
		title: 'EU DDP prepayments (DN)',
		description:
			'DN charges the EU DDP prepayment of an order from the saved payment methods of the customer. When blocked, the gateway refuses new prepayments and returns the message to DN (the status of an already paid order is still returned).',
	},
};

export type TClientAccessFormValues = Record<TAccessAreaKey, TPgClientAccessArea> & {
	easUpdatesPaused: boolean;
	/** In the order of the gateway. */
	notifications: TPgClientNotificationSetting[];
};

export type TClientAccessFormErrors = Partial<Record<TAccessAreaKey, string>>;

const copyArea = (area: TPgClientAccessArea): TPgClientAccessArea => ({
	enabled: area.enabled,
	message: area.message ?? '',
});

export const toValues = (settings: TPgClientAccessSettings): TClientAccessFormValues => ({
	payLink: copyArea(settings.payLink),
	paymentMethods: copyArea(settings.paymentMethods),
	payments: copyArea(settings.payments),
	euDdp: copyArea(settings.euDdp),
	easUpdatesPaused: settings.easUpdatesPaused,
	notifications: settings.notifications.map((n) => ({ ...n })),
});

const areaRequest = (area: TPgClientAccessArea): TPgClientAccessArea => ({
	enabled: area.enabled,
	message: area.message.trim(),
});

export const toRequest = (values: TClientAccessFormValues): TPgClientAccessSettingsRequest => ({
	payLink: areaRequest(values.payLink),
	paymentMethods: areaRequest(values.paymentMethods),
	payments: areaRequest(values.payments),
	euDdp: areaRequest(values.euDdp),
	easUpdatesPaused: values.easUpdatesPaused,
	notifications: values.notifications.map(({ type, enabled }) => ({ type, enabled })),
});

/** The message is required even while the area is open: it is ready for the next block. */
export const validateMessage = (message: string): string | undefined => {
	if (!message.trim()) return 'The message is required';
	if (message.length > ACCESS_MESSAGE_MAX_LENGTH) {
		return `At most ${ACCESS_MESSAGE_MAX_LENGTH} characters`;
	}
	return undefined;
};

export const validate = (values: TClientAccessFormValues): TClientAccessFormErrors => {
	const errors: TClientAccessFormErrors = {};
	ACCESS_AREA_KEYS.forEach((key) => {
		const error = validateMessage(values[key].message);
		if (error) errors[key] = error;
	});
	return errors;
};

export const isAreaChanged = (saved: TPgClientAccessArea, value: TPgClientAccessArea): boolean =>
	saved.enabled !== value.enabled || (saved.message ?? '') !== value.message;

export const isChanged = (
	saved: TPgClientAccessSettings,
	values: TClientAccessFormValues,
): boolean => {
	if (ACCESS_AREA_KEYS.some((key) => isAreaChanged(saved[key], values[key]))) return true;
	if (saved.easUpdatesPaused !== values.easUpdatesPaused) return true;
	const savedEnabled = new Map(saved.notifications.map((n) => [n.type, n.enabled]));
	return values.notifications.some((n) => savedEnabled.get(n.type) !== n.enabled);
};
